/* =========================================================
   PDF -> student rows
   Needs:  npm install pdfjs-dist@3.11.174
   (pinned: v4+ ships an ES-module worker that CRA can't load
   from a CDN URL the same way.)

   Works with text-based PDFs (exported from Excel/Word/Sheets)
   laid out as one student per line/table row, e.g.
     1  Anu Mary  anu@mail.com  9876543210  CMA USA
   Scanned / image-only PDFs have no text layer and can't be read.
   ========================================================= */

const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const PHONE_RE = /\+?\d[\d\s-]{8,16}\d/;
const COURSE_PATTERNS = [
  { label: "CMA USA", re: /\bCMA(\s+USA)?\b/i },
  { label: "ACCA", re: /\bACCA\b/i },
  { label: "EA", re: /\bEA\b/ },
];

export const NAME_RE = /^[\p{L}][\p{L}\s.'-]*$/u;
export const MOBILE_RE = /^\+?\d{10,15}$/;
export const EMAIL_STRICT_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Returns "" when valid, otherwise a message.
export function validateStudent({ fullName = "", email = "", mobile = "" }) {
  const name = fullName.trim();
  if (!name) return "Name is required.";
  if (name.length < 3) return "Name must be at least 3 characters.";
  if (!NAME_RE.test(name)) return "Name: letters, spaces, . ' or - only.";
  if (!EMAIL_STRICT_RE.test(email.trim())) return "Enter a valid email.";
  const m = mobile.replace(/[\s-]/g, "");
  if (m && !MOBILE_RE.test(m)) return "Mobile must be 10-15 digits.";
  return "";
}

export async function extractPdfLines(file) {
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

  const pdf = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  const lines = [];

  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    const content = await page.getTextContent();
    const items = content.items
      .filter((i) => i.str && i.str.trim())
      .map((i) => ({ s: i.str.trim(), x: i.transform[4], y: i.transform[5] }))
      .sort((a, b) => b.y - a.y);

    // Group text fragments that share (roughly) the same baseline into one line.
    const groups = [];
    items.forEach((it) => {
      const last = groups[groups.length - 1];
      if (last && Math.abs(last.y - it.y) <= 3) last.items.push(it);
      else groups.push({ y: it.y, items: [it] });
    });
    groups.forEach((g) =>
      lines.push(g.items.sort((a, b) => a.x - b.x).map((i) => i.s).join("  "))
    );
  }
  return lines;
}

// Every line containing an email becomes a student row; everything else
// (headers, page numbers, titles) is ignored.
export function parseStudentLines(lines) {
  const rows = [];
  lines.forEach((raw) => {
    const emailMatch = raw.match(EMAIL_RE);
    if (!emailMatch) return;

    let rest = raw.replace(emailMatch[0], " ");
    const phoneMatch = rest.match(PHONE_RE);
    const mobile = phoneMatch ? phoneMatch[0].replace(/[\s-]/g, "") : "";
    if (phoneMatch) rest = rest.replace(phoneMatch[0], " ");

    const courses = [];
    COURSE_PATTERNS.forEach(({ label, re }) => {
      if (re.test(rest)) {
        courses.push(label);
        rest = rest.replace(re, " ");
      }
    });

    const fullName = rest
      .replace(/^\s*\d+[.)]?\s+/, "") // leading serial number
      .replace(/[|,;:\t\d]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    rows.push({ fullName, email: emailMatch[0], mobile, courses });
  });
  return rows;
}